import { useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSupplier } from "../../context/SupplierContext";
import { useSellerProfileDrawer } from "../../context/SellerProfileDrawerContext";
import { PageHeader } from "../../components/PortalShell";
import { Button } from "../../components/ui/button";
import api, { money } from "../../lib/api";
import { ArrowUpRight, Mail, Check, Store, Loader2, Send, Copy, Building2, Sparkles, Eye, ExternalLink } from "lucide-react";
import { toast } from "sonner";

/* Buyer's unified supplier hub — verqo-connected + off-platform discovered.
   Acts as the canonical "My suppliers" directory. The off-platform ones are
   still operationally supported (PO inbox + financing) but show a soft
   "Send portal invite" CTA so suppliers can claim a free verqo account. */
export default function BuyerSuppliers() {
  const nav = useNavigate();
  const { open: openProfile } = useSellerProfileDrawer();
  const {
    connections, invites, selectSupplier,
    acceptInvite: acceptInviteCtx, refresh, loading,
  } = useSupplier();

  const [offPlatform, setOffPlatform] = useState([]);
  const [offLoading, setOffLoading] = useState(true);
  const [invitingAll, setInvitingAll] = useState(false);

  const loadOffPlatform = () => {
    setOffLoading(true);
    api.get("/buyer/suppliers/all")
      .then(({ data }) => setOffPlatform(data.filter(s => s.type === "off_platform")))
      .catch(() => setOffPlatform([]))
      .finally(() => setOffLoading(false));
  };

  useEffect(() => { refresh(); loadOffPlatform(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const onAccept = async (token) => {
    try {
      const d = await acceptInviteCtx(token);
      toast.success(`Connected to ${d.tenant_name}`);
      nav("/buyer");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const inviteable = useMemo(() => offPlatform.filter(s => s.status === "not_invited"), [offPlatform]);
  const inviteableSpend = useMemo(
    () => inviteable.reduce((s, x) => s + (x.po_total || 0) + (x.invoice_total || 0), 0),
    [inviteable],
  );
  // Jun 2026 — suppliers the buyer has invited but who haven't accepted
  // yet. Surfaces in a dedicated "Pending invites I sent" section (mirror
  // of the seller-side "Pending invites" section that's already there
  // for invites the buyer RECEIVED). Closes the asymmetry — buyers
  // couldn't see their outbound invites without scanning the giant
  // off-platform table.
  const pendingSent = useMemo(
    () => offPlatform.filter(s => s.status === "invited"),
    [offPlatform],
  );
  // Off-platform table now only shows rows where the buyer has NOT
  // yet invited — pending-sent rows live in their own section above.
  const offPlatformShown = useMemo(
    () => offPlatform.filter(s => s.status !== "invited"),
    [offPlatform],
  );

  const inviteOne = async (s) => {
    try {
      const { data } = await api.post("/buyer/hub/invite-supplier", {
        supplier_email: s.supplier_email, supplier_name: s.supplier_name,
      });
      if (data.already_invited) toast.info("Already invited");
      else toast.success(`Portal invite sent to ${s.supplier_email}`);
      loadOffPlatform();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not invite");
    }
  };
  const inviteAll = async () => {
    if (inviteable.length === 0) return toast.info("Nothing to invite");
    setInvitingAll(true);
    try {
      const { data } = await api.post("/buyer/hub/invite-suppliers/bulk", {
        suppliers: inviteable.map(t => ({ supplier_email: t.supplier_email, supplier_name: t.supplier_name })),
      });
      toast.success(`${data.invited_count} portal invite${data.invited_count === 1 ? "" : "s"} queued`);
      loadOffPlatform();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk invite failed");
    } finally {
      setInvitingAll(false);
    }
  };
  const copyLink = (link) => {
    const full = link?.startsWith("http") ? link : window.location.origin + link;
    navigator.clipboard.writeText(full);
    toast.success("Invite link copied");
  };

  return (
    <>
      <PageHeader title="My suppliers" subtitle="Every supplier you work with — on verqo or off. Open one to enter their portal, or send a PO and operate off-platform from your hub."/>
      <div className="p-8 space-y-10">
        {/* Pending invites */}
        {invites.length > 0 && (
          <section data-testid="buyer-invites-list">
            <h2 className="display text-xl flex items-center gap-2">
              <Mail className="w-4 h-4 text-accent"/>Pending invites
              <span className="text-[11px] mono px-2 py-0.5 rounded-full bg-accent/20 text-foreground">{invites.length}</span>
            </h2>
            <div className="mt-4 grid md:grid-cols-2 gap-4">
              {invites.map(inv => (
                <div key={inv.id} className="rounded-2xl border border-foreground/10 bg-white p-5" data-testid={`invite-${inv.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="display text-lg tracking-tight">{inv.tenant_name}</div>
                      <div className="text-[12px] text-foreground/55 mt-0.5">invited you on {new Date(inv.created_at).toLocaleDateString()}</div>
                    </div>
                    <span className="text-[10.5px] mono uppercase tracking-[0.18em] px-2 py-0.5 rounded border border-foreground/15">{(inv.default_terms || "NET_30").replace("NET_", "Net-")}</span>
                  </div>
                  {inv.additional_credit_limit > 0 && (
                    <div className="mt-3 text-[13px] text-foreground/70">
                      Extra credit offered: <strong>+${inv.additional_credit_limit.toLocaleString()}</strong>
                    </div>
                  )}
                  {inv.note && <p className="text-[13px] text-foreground/60 mt-3 italic">"{inv.note}"</p>}
                  <div className="mt-4 flex items-center gap-2">
                    <Button size="sm" onClick={() => onAccept(inv.token)} data-testid={`invite-accept-${inv.id}`} className="rounded-full bg-foreground text-background">
                      <Check className="w-3.5 h-3.5 mr-1"/>Accept
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Pending invites I SENT (Jun 2026) — outbound invites the
            buyer issued to off-platform suppliers. Same card design as
            the inbound "Pending invites" section for visual symmetry. */}
        {pendingSent.length > 0 && (
          <section data-testid="buyer-pending-sent-list">
            <h2 className="display text-xl flex items-center gap-2">
              <Send className="w-4 h-4 text-violet-600"/>Pending invites I sent
              <span className="text-[11px] mono px-2 py-0.5 rounded-full bg-violet-100 text-violet-800">{pendingSent.length}</span>
            </h2>
            <p className="text-[12.5px] text-foreground/55 mt-1.5 leading-relaxed">
              Suppliers you invited to join verqo. They haven't accepted yet — copy the invite link to nudge them, or wait for them to sign up.
            </p>
            <div className="mt-4 grid md:grid-cols-2 gap-4">
              {pendingSent.map(s => (
                <div key={s.supplier_email}
                     className="rounded-2xl border border-violet-200 bg-violet-50/40 p-5"
                     data-testid={`pending-sent-${s.supplier_email}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="display text-lg tracking-tight truncate">{s.supplier_name}</div>
                      <div className="text-[12px] text-foreground/55 mt-0.5 truncate">{s.supplier_email}</div>
                    </div>
                    <span className="text-[10.5px] mono uppercase tracking-[0.18em] px-2 py-0.5 rounded border border-violet-200 bg-white text-violet-700 shrink-0">
                      Awaiting signup
                    </span>
                  </div>
                  {(s.po_count > 0 || s.invoice_count > 0) && (
                    <div className="mt-3 text-[12px] text-foreground/65">
                      {s.po_count > 0 && <>{s.po_count} PO{s.po_count === 1 ? "" : "s"} · </>}
                      {s.invoice_count > 0 && <>{s.invoice_count} invoice{s.invoice_count === 1 ? "" : "s"} · </>}
                      <strong>{money((s.po_total || 0) + (s.invoice_total || 0))}</strong> volume off-platform
                    </div>
                  )}
                  <div className="mt-4 flex items-center gap-2">
                    {s.invite_code && (
                      <button
                        onClick={() => copyLink(`/register?as=seller&invite=${s.invite_code}`)}
                        data-testid={`copy-link-${s.supplier_email}`}
                        className="text-xs inline-flex items-center gap-1.5 text-foreground/70 hover:text-foreground rounded-full border border-border bg-white px-3 h-8"
                      >
                        <Copy className="w-3 h-3"/>Copy invite link
                      </button>
                    )}
                    <button
                      onClick={() => inviteOne(s)}
                      data-testid={`resend-${s.supplier_email}`}
                      className="text-xs inline-flex items-center gap-1.5 text-foreground/70 hover:text-foreground rounded-full border border-border bg-white px-3 h-8"
                    >
                      <Send className="w-3 h-3"/>Resend invite email
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Active verqo connections */}
        <section>
          <div className="flex items-center gap-2 mb-4">
            <h2 className="display text-xl">On verqo</h2>
            <span className="text-[11px] mono px-2 py-0.5 rounded-full bg-secondary">{connections?.length || 0}</span>
            <span className="text-[10px] mono text-foreground/40 ml-1">Real-time sync · auto-pay · live tracking</span>
          </div>
          {loading && connections.length === 0 ? (
            <div className="text-foreground/50 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin"/>Loading…</div>
          ) : connections.length === 0 ? (
            <div className="rounded-2xl border border-foreground/10 bg-secondary/30 p-10 text-center">
              <Store className="w-6 h-6 mx-auto text-foreground/30"/>
              <p className="text-foreground/55 mt-3">No verqo-connected suppliers yet.</p>
              <p className="text-[12px] text-foreground/45 mt-1">Send POs from your hub — when suppliers join verqo, they'll appear here automatically.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="buyer-connections-list">
              {connections.map(c => {
                const b = c.branding || {};
                const bg = b.primary_color || "#0a1f1c";
                const accent = b.accent_color || "#e8ff3a";
                return (
                  <div
                    key={c.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => { selectSupplier(c); nav("/buyer"); }}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); selectSupplier(c); nav("/buyer"); } }}
                    data-testid={`connection-${c.tenant_slug}`}
                    className="group cursor-pointer rounded-2xl overflow-hidden border border-foreground/10 bg-white hover:shadow-[0_10px_30px_-12px_rgba(10,31,28,0.15)] transition-all"
                  >
                    <div className="p-5" style={{ background: bg, color: "#fff" }}>
                      <div className="flex items-center gap-2.5">
                        {b.logo_url ? (
                          <img src={b.logo_url} alt={c.tenant_name} className="h-7 w-auto"/>
                        ) : (
                          <span className="w-8 h-8 rounded-md grid place-items-center font-bold display" style={{ background: accent, color: bg }}>
                            {(c.tenant_name || "?").slice(0,1)}
                          </span>
                        )}
                        <div className="display text-lg tracking-tight">{c.tenant_name}</div>
                      </div>
                      <div className="mt-6 text-[11px] mono uppercase tracking-[0.18em]" style={{ color: accent }}>
                        {(c.default_terms || "NET_30").replace("NET_", "Net-")}
                        {c.additional_credit_limit > 0 && ` · +$${c.additional_credit_limit.toLocaleString()}`}
                      </div>
                    </div>
                    <div className="p-4 flex items-center justify-between text-[13px]">
                      <span className="text-foreground/55">Enter their portal</span>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); openProfile(c.tenant_slug); }}
                          data-testid={`view-profile-${c.tenant_slug}`}
                          title="View seller profile"
                          className="inline-flex items-center gap-1 text-foreground/55 hover:text-foreground text-[12px]"
                        >
                          <Eye className="w-3.5 h-3.5"/> Profile
                        </button>
                        {c.tenant_slug && (
                          <a
                            href={`/s/${c.tenant_slug}/catalog`}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            data-testid={`view-catalog-${c.tenant_slug}`}
                            title="Open this supplier's catalog as a store webpage in a new tab"
                            className="inline-flex items-center gap-1 text-foreground/55 hover:text-foreground text-[12px]"
                          >
                            <Store className="w-3.5 h-3.5"/> Catalog
                          </a>
                        )}
                        {c.tenant_slug && (
                          <a
                            href={`/s/${c.tenant_slug}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            data-testid={`view-storefront-${c.tenant_slug}`}
                            title="Open this supplier's storefront webpage in a new tab"
                            className="inline-flex items-center gap-1 text-foreground/55 hover:text-foreground text-[12px]"
                          >
                            <ExternalLink className="w-3.5 h-3.5"/> Storefront
                          </a>
                        )}
                        <ArrowUpRight className="w-4 h-4 text-foreground/40 group-hover:text-foreground group-hover:translate-x-0.5 transition-all"/>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* Off-platform discovered suppliers */}
        <section data-testid="off-platform-section">
          <div className="flex items-center gap-2 mb-4">
            <h2 className="display text-xl">Off-platform</h2>
            <span className="text-[11px] mono px-2 py-0.5 rounded-full bg-secondary">{offPlatform.length}</span>
            <span className="text-[10px] mono text-foreground/40 ml-1">Auto-discovered from your POs &amp; invoices</span>
          </div>

          {/* Inviteable banner — soft pitch, not a beg */}
          {inviteable.length > 0 && (
            <div className="rounded-2xl bg-foreground text-background p-5 mb-4 flex items-center justify-between gap-4" data-testid="invite-all-banner">
              <div>
                <div className="text-[10px] mono uppercase tracking-[0.2em] opacity-60">Make your ops faster</div>
                <div className="display text-xl mt-1">Invite {inviteable.length} supplier{inviteable.length === 1 ? "" : "s"} to claim their portal</div>
                <div className="text-xs opacity-70 mt-1 max-w-md">
                  Free for them. Unlocks real-time tracking, auto-pay, and instant invoicing on {money(inviteableSpend)} of your annual spend.
                </div>
              </div>
              <Button onClick={inviteAll} disabled={invitingAll} data-testid="invite-all-btn"
                className="rounded-full bg-background text-foreground hover:bg-background/90 px-5">
                <Send className="w-4 h-4 mr-1.5"/>{invitingAll ? "Sending…" : "Invite all"}
              </Button>
            </div>
          )}

          {offLoading ? (
            <div className="text-foreground/50 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin"/>Loading…</div>
          ) : offPlatformShown.length === 0 ? (
            <div className="rounded-2xl border border-foreground/10 bg-secondary/30 p-10 text-center" data-testid="off-platform-empty">
              <Building2 className="w-6 h-6 mx-auto text-foreground/30"/>
              <p className="text-foreground/55 mt-3">No off-platform suppliers tracked yet.</p>
              <p className="text-[12px] text-foreground/45 mt-1">Send a PO from <a href="/buyer/po-inbox" className="underline">PO Inbox</a> and the supplier will auto-appear here.</p>
            </div>
          ) : (
            <div className="rounded-2xl bg-white border border-border overflow-hidden" data-testid="off-platform-list">
              <table className="w-full text-sm">
                <thead className="bg-secondary/50 text-left text-xs uppercase tracking-widest text-foreground/60">
                  <tr>
                    <th className="px-5 py-3">Supplier</th>
                    <th className="px-5 py-3">Status</th>
                    <th className="px-5 py-3">POs</th>
                    <th className="px-5 py-3">Invoices</th>
                    <th className="px-5 py-3">Volume</th>
                    <th className="px-5 py-3 text-right">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {offPlatformShown.map(s => (
                    <tr key={s.supplier_email} data-testid={`off-row-${s.supplier_email}`}>
                      <td className="px-5 py-3">
                        <div className="font-medium">{s.supplier_name}</div>
                        <div className="text-xs text-foreground/55">{s.supplier_email}</div>
                      </td>
                      <td className="px-5 py-3">
                        {s.status === "not_invited" && <span className="chip text-[11px] bg-secondary text-foreground/60 border border-border">Off-platform</span>}
                        {s.status === "on_verqo_not_connected" && <span className="chip text-[11px] bg-blue-50 text-blue-700 border border-blue-200">On verqo</span>}
                      </td>
                      <td className="px-5 py-3 text-xs">{s.po_count}</td>
                      <td className="px-5 py-3 text-xs">{s.invoice_count}</td>
                      <td className="px-5 py-3 display">{money((s.po_total || 0) + (s.invoice_total || 0))}</td>
                      <td className="px-5 py-3 text-right space-x-1">
                        {s.status === "not_invited" && (
                          <Button size="sm" onClick={() => inviteOne(s)} data-testid={`invite-${s.supplier_email}`}
                            className="rounded-full bg-foreground text-background hover:bg-foreground/90 h-8 px-3">
                            <Send className="w-3.5 h-3.5 mr-1"/>Invite
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <div className="rounded-2xl bg-secondary/30 border border-border p-4 flex items-start gap-3 text-xs text-foreground/65">
          <Sparkles className="w-4 h-4 text-foreground/50 shrink-0 mt-0.5"/>
          <div>
            <span className="font-medium text-foreground">One directory. Two operating modes. </span>
            On-verqo suppliers give you real-time sync, automated payments, and live shipment tracking. Off-platform ones still flow through your <a href="/buyer/po-inbox" className="underline">PO Inbox</a> and <a href="/buyer/hub/financing" className="underline">financing</a> — verqo becomes their de-facto portal whether they sign up or not.
          </div>
        </div>
      </div>
    </>
  );
}
